import React from "react";
import { Award, Building2, Target } from "lucide-react";
import { COLORS } from "../../utils/constants";
import { formatCurrency } from "../../utils/formatters";
import { SeuilBar } from "./SeuilBar";

const Bloc = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border p-4 bg-white space-y-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.text }}><Icon size={16} /> {title}</div>
    {children}
  </div>
);

const Trimestres = ({ t, annee }) => (
  <Bloc icon={Award} title={`Trimestres retraite ${annee}`} testid={`simulateur-trimestres-${annee}`}>
    <div className="flex gap-2">
      {[1, 2, 3, 4].map((n) => {
        const acquis = n <= t.avant;
        const gagne = !acquis && n <= t.apres;
        return (
          <div key={n} className="flex-1 h-10 rounded-lg flex items-center justify-center text-xs font-bold border"
            style={acquis ? { backgroundColor: COLORS.text, color: "white" } : gagne ? { backgroundColor: COLORS.success, color: "white" } : { color: "#9CA3AF" }}>
            T{n}
          </div>
        );
      })}
    </div>
    <p className="text-sm" data-testid={`simulateur-trimestres-texte-${annee}`}>
      {t.gagnes > 0
        ? <><strong style={{ color: COLORS.success }}>+{t.gagnes} trimestre{t.gagnes > 1 ? "s" : ""}</strong> grâce au contrat ({t.avant} → {t.apres} sur 4).</>
        : t.apres >= 4 ? <>Tes 4 trimestres de l'année sont déjà validés : le contrat n'en ajoute pas.</>
        : <>Pas de trimestre en plus : il manquera encore {formatCurrency(t.ca_manquant_prochain)} de CA pour le suivant.</>}
    </p>
    <p className="text-[11px] text-muted-foreground">CA de l'année : {formatCurrency(t.ca_avant)} → {formatCurrency(t.ca_apres)}. Maximum 4 trimestres par an.</p>
  </Bloc>
);

const Cfe = ({ c }) => (
  <Bloc icon={Building2} title={`CFE ${c.annee_cfe}`} testid={`simulateur-cfe-${c.annee_cfe}`}>
    <div className="grid grid-cols-2 gap-3 text-center">
      {[["Sans le contrat", c.avant], ["Avec le contrat", c.apres]].map(([l, v]) => (
        <div key={l} className="rounded-lg p-2" style={{ backgroundColor: COLORS.bg }}>
          <div className="text-[11px] text-muted-foreground">{l}</div>
          <div className="text-lg font-bold" style={{ color: COLORS.text }}>{v.exonere ? "Exonéré" : `≈ ${formatCurrency(v.montant)}`}</div>
          {!v.exonere && <div className="text-[10px] text-muted-foreground">{formatCurrency(v.min)} à {formatCurrency(v.max)}</div>}
        </div>
      ))}
    </div>
    <p className="text-sm" data-testid={`simulateur-cfe-texte-${c.annee_cfe}`}>
      {c.ecart > 0 ? <>La CFE augmenterait d'environ <strong>{formatCurrency(c.ecart)}</strong>.</> : <>Pas de changement de tranche de CFE.</>}
    </p>
    <p className="text-[11px] text-muted-foreground">La CFE {c.annee_cfe} est calculée sur ton CA {c.annee_cfe - 2} ({formatCurrency(c.ca_reference_avant)} → {formatCurrency(c.ca_reference_apres)}). Moyenne nationale : le montant exact dépend de la base et du taux votés par ta commune.</p>
  </Bloc>
);

export const AnneeImpact = ({ a }) => {
  const pl = a.plafond;
  const tva = a.tva;
  const alertePl = pl.depasse ? `dépassé${pl.mois_depassement ? ` en ${pl.mois_depassement}` : ""}` : null;
  const alerteTva = tva.assujetti_des_debut ? "TVA due dès janvier"
    : tva.mois_majore ? `TVA due dès ${tva.mois_majore}`
    : tva.deja_depasse ? "déjà dépassé"
    : tva.franchit ? `franchi${tva.mois_franchissement ? ` en ${tva.mois_franchissement}` : ""} → TVA au 1er janvier ${a.annee + 1}` : null;
  return (
    <section className="flex flex-col gap-3" data-testid={`simulateur-annee-${a.annee}`}>
      <h2 className="text-base md:text-lg font-semibold" style={{ color: COLORS.text }}>
        Impact sur {a.annee} <span className="text-sm font-normal text-muted-foreground">— {formatCurrency(a.part_contrat)} encaissés cette année-là</span>
      </h2>
      {a.contrat_seul && (
        <p className="text-xs rounded-lg p-2" style={{ backgroundColor: COLORS.warning + "15", color: COLORS.warning }}>
          Pour {a.annee}, seul ce contrat est compté : tes autres ventes de {a.annee} ne sont pas encore connues.
        </p>
      )}
      <Bloc icon={Target} title="Tes seuils" testid={`simulateur-seuils-${a.annee}`}>
        <SeuilBar label="Plafond micro-entreprise" seuil={pl.plafond} avant={pl.pourcentage_avant} apres={pl.pourcentage_apres} reste={pl.reste_apres} alerte={alertePl} testid={`simulateur-plafond-${a.annee}`} />
        <SeuilBar label={tva.libelle} seuil={tva.seuil_base} avant={tva.pourcentage_avant} apres={tva.pourcentage_apres} reste={tva.reste_apres} alerte={alerteTva} testid={`simulateur-tva-${a.annee}`} />
        <p className="text-[11px] text-muted-foreground">CA {a.annee} de cette activité : {formatCurrency(a.ca_avant)} → {formatCurrency(a.ca_apres)}</p>
      </Bloc>
      <div className="grid md:grid-cols-2 gap-3">
        <Trimestres t={a.trimestres} annee={a.annee} />
        <Cfe c={a.cfe} />
      </div>
    </section>
  );
};

export default AnneeImpact;
